// Cart Sample Data with Complete TypeScript Types
// This file includes all necessary types for working on a separate branch

import { CartType } from '@monorepo/types';

// ===== TYPE DEFINITIONS =====

//TODO delete this file after we have the real data, and import the real types from the types folder
export enum ProductType {
  Program = 'program',
  AddOn = 'add-on',
  Credit = 'credit',
  General = 'general',
}

export enum TradingSystem {
  MatchTrader = 'match-trader',
  TraderEvolution = 'trader-evolution',
  MetaTraderFive = 'meta-trader-5',
  MetaTraderXP = 'meta-trader-xp',
  CTrader = 'c-trader',
  MetaTraderContest = 'meta-trader-contest',
}

export enum OptionalAssets {
  Forex = 'forex',
  Futures = 'futures',
  DayTrade = 'dayTrade',
  Swing = 'swing',
  QuickBuy = 'quickBuy',
}

export enum SupportedCurrencies {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  INR = 'INR',
}

export enum CartStep {
  Assets = 'assets',
  Programs = 'programs',
  Billing = 'billing',
}

export enum DiscountType {
  Coupon = 'coupon',
  Affiliate = 'affiliate',
}

export interface ProgramMetadata {
  productName?: string;
  tradingSystem: TradingSystem;
}

export interface AddOnMetadata {
  productName?: string;
  sourceId: string;
}

export interface CartItemEntity {
  productId: string;
  productType: ProductType;
  quantity: number;
  unitPrice: number;
  metadata: ProgramMetadata | AddOnMetadata;
}

export interface CartAssetEntity {
  totalPrice: number;
  originalPrice: number;
  items: CartItemEntity[];
}

export interface DiscountMetadata {
  discountType?: DiscountType;
  discountSourceId?: string;
  discountPercentage?: number;
  credits?: number;
  discountExpiration?: Date;
}

export interface BillingAddress {
  address: string;
  city: string;
  zipCode: string;
  country: string;
  state: string;
}

export interface BillingInformation {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  countryCode: string;
  billingAddress: BillingAddress;
}

export interface CartEntity {
  userId: string;
  currency: SupportedCurrencies;
  step: CartStep;
  synced: boolean;
  discountMetadata?: DiscountMetadata;
  billingInformation?: BillingInformation;
  forex?: CartAssetEntity;
  futures?: CartAssetEntity;
  dayTrade?: CartAssetEntity;
  swing?: CartAssetEntity;
  quickBuy?: CartAssetEntity;
}

// ===== SAMPLE DATA =====

// Sample Cart Data - Full cart with multiple assets and discount
export const SAMPLE_CART_DATA: CartEntity = {
  userId: 'user_12345',
  currency: SupportedCurrencies.USD,
  step: CartStep.Billing,
  synced: true,

  // Discount information (optional)
  discountMetadata: {
    discountType: DiscountType.Coupon,
    discountSourceId: 'SAVE20',
    discountPercentage: 20,
    credits: 0,
    discountExpiration: new Date('2024-12-31T23:59:59Z'),
  },

  // Billing information (optional)
  billingInformation: {
    email: 'john.doe@example.com',
    firstName: 'John',
    lastName: 'Doe',
    phoneNumber: '+1234567890',
    countryCode: 'US',
    billingAddress: {
      address: '123 Main Street',
      city: 'New York',
      zipCode: '10001',
      country: 'United States',
      state: 'NY',
    },
  },

  // Forex asset cart
  forex: {
    originalPrice: 500.0,
    totalPrice: 400.0, // After 20% discount
    items: [
      {
        productId: 'program_forex_001',
        productType: ProductType.Program,
        quantity: 1,
        unitPrice: 500.0,
        metadata: {
          productName: 'Forex Trading Program',
          tradingSystem: TradingSystem.MetaTraderFive,
        },
      },
    ],
  },

  // Futures asset cart
  futures: {
    originalPrice: 750.0,
    totalPrice: 600.0, // After 20% discount
    items: [
      {
        productId: 'program_futures_001',
        productType: ProductType.Program,
        quantity: 1,
        unitPrice: 600.0,
        metadata: {
          productName: 'Futures Trading Program',
          tradingSystem: TradingSystem.TraderEvolution,
        },
      },
      {
        productId: 'addon_futures_001',
        productType: ProductType.AddOn,
        quantity: 1,
        unitPrice: 150.0,
        metadata: {
          productName: 'Advanced Analytics Add-on',
          sourceId: 'analytics_pro_001',
        },
      },
    ],
  },

  // Day Trading asset cart
  dayTrade: {
    originalPrice: 300.0,
    totalPrice: 300.0, // No discount applied
    items: [
      {
        productId: 'program_daytrade_001',
        productType: ProductType.Program,
        quantity: 1,
        unitPrice: 300.0,
        metadata: {
          productName: 'Day Trading Program',
          tradingSystem: TradingSystem.MatchTrader,
        },
      },
    ],
  },

  // Swing Trading asset cart (empty)
  swing: undefined,

  // Quick Buy asset cart (empty)
  quickBuy: undefined,
};

// Sample Cart Data - Empty cart (new user)
export const SAMPLE_CART_DATA_EMPTY: CartEntity = {
  userId: 'user_67890',
  currency: SupportedCurrencies.EUR,
  step: CartStep.Assets,
  synced: true,
  discountMetadata: undefined,
  billingInformation: undefined,
  forex: undefined,
  futures: undefined,
  dayTrade: undefined,
  swing: undefined,
  quickBuy: undefined,
};

// Sample Cart Data - Cart with credits discount
export const SAMPLE_CART_DATA_WITH_CREDITS: CartEntity = {
  userId: 'user_11111',
  currency: SupportedCurrencies.GBP,
  step: CartStep.Billing,
  synced: true,

  discountMetadata: {
    discountType: DiscountType.Affiliate,
    discountSourceId: 'affiliate_001',
    discountPercentage: 0,
    credits: 50, // User has 50 credits to use
    discountExpiration: new Date('2024-12-31T23:59:59Z'),
  },

  billingInformation: {
    email: 'jane.smith@example.com',
    firstName: 'Jane',
    lastName: 'Smith',
    phoneNumber: '+44123456789',
    countryCode: 'GB',
    billingAddress: {
      address: '456 High Street',
      city: 'London',
      zipCode: 'SW1A 1AA',
      country: 'United Kingdom',
      state: 'England',
    },
  },

  forex: {
    originalPrice: 400.0,
    totalPrice: 350.0, // After 50 credits applied
    items: [
      {
        productId: 'program_forex_002',
        productType: ProductType.Program,
        quantity: 1,
        unitPrice: 400.0,
        metadata: {
          productName: 'Premium Forex Program',
          tradingSystem: TradingSystem.CTrader,
        },
      },
    ],
  },

  futures: undefined,
  dayTrade: undefined,
  swing: undefined,
  quickBuy: undefined,
};

// Sample Cart Data - Cart with multiple items in one asset
export const SAMPLE_CART_DATA_MULTIPLE_ITEMS: CartEntity = {
  userId: 'user_22222',
  currency: SupportedCurrencies.USD,
  step: CartStep.Programs,
  synced: true,

  discountMetadata: {
    discountType: DiscountType.Coupon,
    discountSourceId: 'BUNDLE15',
    discountPercentage: 15,
    credits: 0,
    discountExpiration: new Date('2024-12-31T23:59:59Z'),
  },

  billingInformation: undefined,

  forex: {
    originalPrice: 1200.0,
    totalPrice: 1020.0, // After 15% discount
    items: [
      {
        productId: 'program_forex_003',
        productType: ProductType.Program,
        quantity: 1,
        unitPrice: 800.0,
        metadata: {
          productName: 'Professional Forex Program',
          tradingSystem: TradingSystem.MetaTraderFive,
        },
      },
      {
        productId: 'addon_forex_001',
        productType: ProductType.AddOn,
        quantity: 1,
        unitPrice: 200.0,
        metadata: {
          productName: 'Risk Management Add-on',
          sourceId: 'risk_mgmt_001',
        },
      },
      {
        productId: 'addon_forex_002',
        productType: ProductType.AddOn,
        quantity: 2,
        unitPrice: 100.0,
        metadata: {
          productName: 'Market Analysis Tool',
          sourceId: 'market_analysis_001',
        },
      },
    ],
  },

  futures: undefined,
  dayTrade: undefined,
  swing: undefined,
  quickBuy: undefined,
};

// ===== UTILITY FUNCTIONS =====

// Helper function to get total cart value across all assets
export const getTotalCartValue = (cart: CartEntity): number => {
  let total = 0;

  if (cart.forex) total += cart.forex.totalPrice;
  if (cart.futures) total += cart.futures.totalPrice;
  if (cart.dayTrade) total += cart.dayTrade.totalPrice;
  if (cart.swing) total += cart.swing.totalPrice;
  if (cart.quickBuy) total += cart.quickBuy.totalPrice;

  return total;
};

// Helper function to get total original value across all assets
export const getTotalOriginalValue = (cart: CartEntity): number => {
  let total = 0;

  if (cart.forex) total += cart.forex.originalPrice;
  if (cart.futures) total += cart.futures.originalPrice;
  if (cart.dayTrade) total += cart.dayTrade.originalPrice;
  if (cart.swing) total += cart.swing.originalPrice;
  if (cart.quickBuy) total += cart.quickBuy.originalPrice;

  return total;
};

// Helper function to get total savings
export const getTotalSavings = (cart: CartEntity): number => {
  return getTotalOriginalValue(cart) - getTotalCartValue(cart);
};

// Helper function to get all items across all assets
export const getAllCartItems = (cart: CartEntity): CartItemEntity[] => {
  const items: CartItemEntity[] = [];

  if (cart.forex) items.push(...cart.forex.items);
  if (cart.futures) items.push(...cart.futures.items);
  if (cart.dayTrade) items.push(...cart.dayTrade.items);
  if (cart.swing) items.push(...cart.swing.items);
  if (cart.quickBuy) items.push(...cart.quickBuy.items);

  return items;
};

// Helper function to check if cart has items
export const hasCartItems = (cart: CartEntity): boolean => {
  return getAllCartItems(cart).length > 0;
};

// Helper function to get asset names that have items
export const getActiveAssets = (cart: CartType): OptionalAssets[] => {
  const activeAssets: OptionalAssets[] = [];

  if (cart.forex && cart.forex.items.length > 0)
    activeAssets.push(OptionalAssets.Forex);
  if (cart.futures && cart.futures.items.length > 0)
    activeAssets.push(OptionalAssets.Futures);
  if (cart.dayTrade && cart.dayTrade.items.length > 0)
    activeAssets.push(OptionalAssets.DayTrade);
  if (cart.swing && cart.swing.items.length > 0)
    activeAssets.push(OptionalAssets.Swing);
  if (cart.quickBuy && cart.quickBuy.items.length > 0)
    activeAssets.push(OptionalAssets.QuickBuy);

  return activeAssets;
};
